import { withAlpha } from "../../common/color.js";
import type { MouseEventArgs, KeyEventArgs } from "../core/events.js";
import { clamp, type Rect, type Size } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { DrawableTexture, RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { ScrollableBase, type ScrollableOptions } from "../controls/ScrollableBase.js";

export interface SaveSlot {
  id: string;
  /** "Chapter 3 — The Drowned Road", or "Autosave". */
  title: string;
  /** Where the player was. */
  location?: string;
  /** Epoch milliseconds the save was written. */
  savedAt?: number;
  /** Seconds played. */
  playtime?: number;
  /** A screenshot from the moment it was saved. */
  thumbnail?: DrawableTexture | null;
  thumbnailSource?: Rect;
  /** An empty slot: "— Empty —", and only usable for saving. */
  empty?: boolean;
}

export interface SaveSlotListOptions extends ScrollableOptions {
  slots?: readonly SaveSlot[];
  /** Height of one row. */
  rowHeight?: number;
  /** Width of the thumbnail; its height fills the row. */
  thumbnailWidth?: number;
  font?: FontSpec | null;
  titleFont?: FontSpec | null;
  onSelect?: (slot: SaveSlot, index: number) => void;
  onActivate?: (slot: SaveSlot, index: number) => void;
}

const DEFAULT_ROW_HEIGHT = 68;
const DEFAULT_THUMB_WIDTH = 108;
const PADDING = 8;

/**
 * The load-game screen: a scrolling list of saves, each with a thumbnail, a
 * name, where you were and how long you have played.
 *
 * A `ListView` with columns is the wrong shape for this — a save is a card,
 * not a row of fields, and the thumbnail is most of what the player is
 * actually reading. Rows are a fixed height, so the list virtualises: a
 * hundred saves cost the same to draw as five.
 */
export class SaveSlotList extends ScrollableBase {
  #slots: SaveSlot[];
  #selected = -1;
  #hovered = -1;

  rowHeight: number;
  thumbnailWidth: number;
  font: FontSpec | null;
  titleFont: FontSpec | null;

  constructor(options: SaveSlotListOptions = {}) {
    super({ width: 420, height: 300, ...options });
    this.#slots = [...(options.slots ?? [])];
    this.rowHeight = options.rowHeight ?? DEFAULT_ROW_HEIGHT;
    this.thumbnailWidth = options.thumbnailWidth ?? DEFAULT_THUMB_WIDTH;
    this.font = options.font ?? null;
    this.titleFont = options.titleFont ?? null;
    this.tabStop = true;

    if (options.onSelect) {
      this.on("selectionchanged", () => {
        const slot = this.#slots[this.#selected];
        if (slot) options.onSelect!(slot, this.#selected);
      });
    }
    if (options.onActivate) {
      this.on("itemactivate", (index) => {
        const slot = this.#slots[index];
        if (slot) options.onActivate!(slot, index);
      });
    }

    this.on("pointermove", (e) => this.#setHovered(this.indexAt(e.x, e.y)));
    this.on("pointerleave", () => this.#setHovered(-1));
    this.on("click", (e) => this.#onClick(e));
    this.on("doubleclick", (e) => {
      const index = this.indexAt(e.x, e.y);
      if (index >= 0) this.activate(index);
    });
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  get slots(): readonly SaveSlot[] {
    return this.#slots;
  }
  set slots(v: readonly SaveSlot[]) {
    this.#slots = [...v];
    this.#selected = clamp(this.#selected, -1, this.#slots.length - 1);
    this.updateScrollBars();
    this.emit("change");
    this.invalidate();
  }

  get selectedIndex(): number {
    return this.#selected;
  }
  set selectedIndex(index: number) {
    const next = index < 0 || index >= this.#slots.length ? -1 : index;
    if (next === this.#selected) return;
    this.#selected = next;
    this.scrollRowIntoView(next);
    this.emit("selectionchanged");
    this.invalidate();
  }

  get selected(): SaveSlot | null {
    return this.#slots[this.#selected] ?? null;
  }

  /** Load, or overwrite — whichever the screen is for. */
  activate(index: number): boolean {
    const slot = this.#slots[index];
    if (!slot) return false;
    this.selectedIndex = index;
    this.emit("itemactivate", index);
    return true;
  }

  /** The row containing a local point, or -1. */
  indexAt(_x: number, y: number): number {
    const view = this.viewportRect;
    if (this.rowHeight <= 0) return -1;
    const index = Math.floor((y - view.y + this.scrollOffsetY) / this.rowHeight);
    return index >= 0 && index < this.#slots.length ? index : -1;
  }

  /** Scroll a row into view. Named apart from the base's rect-taking one. */
  scrollRowIntoView(index: number): void {
    if (index < 0) return;
    const view = this.viewportRect;
    const top = index * this.rowHeight;
    if (top < this.scrollOffsetY) this.scrollOffsetY = top;
    else if (top + this.rowHeight > this.scrollOffsetY + view.height) {
      this.scrollOffsetY = top + this.rowHeight - view.height;
    }
  }

  protected override contentSize(): Size {
    return { width: 0, height: this.#slots.length * this.rowHeight };
  }

  #setHovered(index: number): void {
    if (index === this.#hovered) return;
    this.#hovered = index;
    this.invalidate();
  }

  #onClick(e: MouseEventArgs): void {
    const index = this.indexAt(e.x, e.y);
    if (index < 0) return;
    this.focus();
    this.selectedIndex = index;
  }

  #onKeyDown(e: KeyEventArgs): void {
    const count = this.#slots.length;
    if (count === 0) return;
    switch (e.key) {
      case "ArrowDown":
        this.selectedIndex = Math.min(count - 1, this.#selected + 1);
        e.handled = true;
        break;
      case "ArrowUp":
        this.selectedIndex = Math.max(0, this.#selected - 1);
        e.handled = true;
        break;
      case "Home":
        this.selectedIndex = 0;
        e.handled = true;
        break;
      case "End":
        this.selectedIndex = count - 1;
        e.handled = true;
        break;
      case "Enter":
      case " ":
        if (this.activate(this.#selected)) e.handled = true;
        break;
      default:
        break;
    }
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const state = this.visualState();
    theme.drawListBackground(rc, this.localRect, state);

    const view = this.viewportRect;
    if (view.width <= 0 || view.height <= 0) return;
    const p = theme.palette;
    const font = this.font ?? theme.metrics.font;
    const titleFont = this.titleFont ?? theme.metrics.captionFont;

    rc.clipped(view, () => {
      const first = Math.max(0, Math.floor(this.scrollOffsetY / this.rowHeight));
      const last = Math.min(
        this.#slots.length - 1,
        Math.ceil((this.scrollOffsetY + view.height) / this.rowHeight),
      );

      for (let i = first; i <= last; i++) {
        const slot = this.#slots[i];
        if (!slot) continue;
        const row: Rect = {
          x: view.x,
          y: view.y + i * this.rowHeight - this.scrollOffsetY,
          width: view.width,
          height: this.rowHeight,
        };
        theme.drawListItem(rc, row, {
          ...state,
          hovered: i === this.#hovered,
          selected: i === this.#selected,
          active: this.focused,
        });

        let x = row.x + PADDING;
        if (!slot.empty) {
          const thumb: Rect = {
            x,
            y: row.y + PADDING,
            width: this.thumbnailWidth,
            height: row.height - PADDING * 2,
          };
          if (slot.thumbnail) rc.drawTexture(slot.thumbnail, thumb, slot.thumbnailSource);
          else rc.fillRect(thumb, withAlpha(p.desktop, 0.6), 2);
          x += this.thumbnailWidth + PADDING;
        }

        const ink = slot.empty ? p.textDisabled : p.text;
        rc.drawText(slot.empty ? "— Empty —" : slot.title, x, row.y + PADDING, titleFont, ink, {
          baseline: "top",
          maxWidth: Math.max(0, row.x + row.width - x - PADDING),
        });

        if (slot.empty) continue;
        let y = row.y + PADDING + rc.lineHeight(titleFont);
        if (slot.location) {
          rc.drawText(slot.location, x, y, font, p.textSubtle, {
            baseline: "top",
            maxWidth: Math.max(0, row.x + row.width - x - PADDING),
          });
          y += rc.lineHeight(font);
        }
        const meta = [
          slot.playtime !== undefined ? formatPlaytime(slot.playtime) : null,
          slot.savedAt !== undefined ? formatSavedAt(slot.savedAt) : null,
        ]
          .filter(Boolean)
          .join("   ");
        if (meta) {
          rc.drawText(meta, x, y, font, p.textSubtle, { baseline: "top" });
        }
      }
    });
  }
}

/** "12h 34m", or "8m" for a short run. */
export function formatPlaytime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

/** A save's timestamp, in the reader's own locale. */
export function formatSavedAt(at: number): string {
  const d = new Date(at);
  const date = d.toLocaleDateString();
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${date} ${time}`;
}
